import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, AlertTriangle, Check, Loader2, Car, MessageCircle, Phone, DollarSign, Footprints, ClipboardCheck, ArrowRight, Flame, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DEMO_TYPE_LABELS, NEXT_STEP_LABELS, STAGE_LABELS } from '@/types';
import type { DemoType, NextStep } from '@/types';
import { toast } from 'sonner';
import { TemplateChips } from '@/components/TemplateChips';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/telemetry';
import { useSubscription } from '@/hooks/useSubscription';
import { NewDealDialog } from '@/components/NewDealDialog';
import { QuickCaptureVoiceButton } from '@/components/QuickCaptureVoiceButton';
import { timeAgo } from '@/data/mockData';

// Maps the 6 most-used rep workflow steps to (activity type → suggested next step).
// One tap pre-fills both fields; the rep just hits Save.
const ONE_TAP_TILES: { type: DemoType; label: string; icon: typeof Zap; nextStep: NextStep }[] = [
  { type: 'walkaround',      label: 'Walkaround',   icon: Footprints,    nextStep: 'appointment' },
  { type: 'test_drive',      label: 'Test Drive',   icon: Car,           nextStep: 'send_numbers' },
  { type: 'quote_review',    label: 'Quote Review', icon: DollarSign,    nextStep: 'close' },
  { type: 'phone_call',      label: 'Call Logged',  icon: Phone,         nextStep: 'follow_up' },
  { type: 'followup_video',  label: 'Follow-Up',    icon: MessageCircle, nextStep: 'follow_up' },
  { type: 'appointment_set', label: 'Appt Set',     icon: ClipboardCheck, nextStep: 'appointment' },
];

// Dynamic CTA labels — momentum > generic "Save".
const SAVE_LABELS: Record<DemoType, string> = {
  walkaround: 'Log Walkaround',
  test_drive: 'Complete Test Drive',
  quote_review: 'Log Quote Review',
  phone_call: 'Log Call',
  followup_video: 'Save Follow-Up',
  appointment_set: 'Log Appointment',
};

export default function QuickLog() {
  const navigate = useNavigate();
  const { mode, userId, dealershipId, role } = useAuth();
  const { deals, activities, refresh } = useDealershipData();
  const { hasAccess, loading: subLoading } = useSubscription();
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [customer, setCustomer] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [demoType, setDemoType] = useState<DemoType | ''>('');
  const [notes, setNotes] = useState('');
  const [nextStep, setNextStep] = useState<NextStep | ''>('');
  const [showWarning, setShowWarning] = useState(false);
  const [saving, setSaving] = useState(false);

  const isReal = mode === 'real';
  const selectedDeal = deals.find(d => d.id === selectedDealId);
  const dashboardPath = role === 'manager' || role === 'admin' ? '/dashboard' : '/rep';

  const lastActivityForDeal = useMemo(() => {
    if (!selectedDealId) return null;
    return activities.find(a => a.deal_id === selectedDealId) ?? null;
  }, [activities, selectedDealId]);

  const saveLabel = demoType ? SAVE_LABELS[demoType as DemoType] : 'Save Activity';

  const handleQuickTile = (type: DemoType, ns: NextStep) => {
    setDemoType(type);
    setNextStep(ns);
    setShowWarning(false);
  };

  const handleSave = async () => {
    if (isReal && !subLoading && !hasAccess) {
      toast.error('Your trial has ended', {
        description: 'Start a plan to log activities and create deals.',
        action: { label: 'See plans', onClick: () => navigate('/pricing') },
      });
      return;
    }
    if (!nextStep) {
      setShowWarning(true);
      return;
    }
    if (!demoType) {
      toast.error('Pick an activity type');
      return;
    }

    // Demo mode: keep the original toast-only behaviour
    if (!isReal) {
      toast.success('Activity logged successfully!', {
        description: `${customer || 'Customer'} — ${DEMO_TYPE_LABELS[demoType as DemoType]} logged.`,
      });
      navigate(dashboardPath);
      return;
    }

    // Real mode: must have a deal (RLS + schema require deal_id NOT NULL)
    if (!selectedDealId || !selectedDeal) {
      toast.error('Pick a deal to log this activity against');
      return;
    }
    if (!dealershipId || !userId) {
      toast.error('Sign-in required');
      return;
    }

    setSaving(true);
    const detailParts = [DEMO_TYPE_LABELS[demoType as DemoType]];
    if (notes.trim()) detailParts.push(notes.trim());
    const details = detailParts.join(' — ').slice(0, 500);

    const { error: actErr } = await supabase.from('activities').insert({
      dealership_id: dealershipId,
      deal_id: selectedDealId,
      rep_id: userId,
      activity_type: demoType,
      details,
      customer_name: selectedDeal.customer_name,
    });

    if (actErr) {
      setSaving(false);
      toast.error('Failed to log activity', { description: actErr.message });
      return;
    }

    // Update deal next_step + last_activity_at
    const { error: dealErr } = await supabase
      .from('deals')
      .update({ next_step: nextStep, last_activity_at: new Date().toISOString(), status: 'active' })
      .eq('id', selectedDealId);

    setSaving(false);
    if (dealErr) {
      toast.warning('Activity saved, but next step did not update', { description: dealErr.message });
    } else {
      toast.success('Activity logged');
    }
    track('activity_logged', { userId, dealershipId, mode }, { activity_type: demoType });
    track('next_step_set', { userId, dealershipId, mode }, { next_step: nextStep });
    refresh();
    navigate(dashboardPath);
  };

  return (
    <div className="max-w-lg mx-auto space-y-4 pb-20 lg:pb-0">
      <div className="flex items-center gap-3">
        <Zap className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Quick Log</h1>
          <p className="text-sm text-muted-foreground">Log activity in seconds</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          {isReal && deals.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">One-tap log</label>
              <div className="grid grid-cols-3 gap-2">
                {ONE_TAP_TILES.map((t) => {
                  const active = demoType === t.type;
                  return (
                    <button
                      key={t.type}
                      type="button"
                      onClick={() => handleQuickTile(t.type, t.nextStep)}
                      className={`flex flex-col items-center justify-center gap-1 h-20 rounded-lg border text-xs font-medium transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-secondary text-secondary-foreground border-border hover:bg-accent'
                      }`}
                    >
                      <t.icon className="h-5 w-5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Pick a tile, pick a deal, save. No typing.</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Apply a template (optional)</label>
            {deals.length > 0 && (
              <Select value={selectedDealId} onValueChange={setSelectedDealId}>
                <SelectTrigger><SelectValue placeholder="Select a deal" /></SelectTrigger>
                <SelectContent>
                  {deals.slice(0, 25).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.customer_name} — {d.vehicle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedDeal && (
              <div className="rounded-md border border-border bg-muted/30 p-2.5 text-xs space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Last:&nbsp;</span>
                  {lastActivityForDeal ? (
                    <span className="text-foreground">
                      {DEMO_TYPE_LABELS[lastActivityForDeal.activity_type as DemoType] ?? lastActivityForDeal.activity_type.replace(/_/g, ' ')}
                      {' · '}{timeAgo(lastActivityForDeal.created_at)}
                    </span>
                  ) : <span className="text-foreground">No activity yet</span>}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ArrowRight className="h-3 w-3" />
                  <span>Next:&nbsp;</span>
                  <span className={selectedDeal.next_step ? 'text-foreground' : 'text-red-400'}>
                    {selectedDeal.next_step ? NEXT_STEP_LABELS[selectedDeal.next_step] : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Flame className={`h-3 w-3 ${selectedDeal.priority === 'hot' ? 'text-primary' : ''}`} />
                  <span>Stage:&nbsp;</span>
                  <span className="text-foreground">{STAGE_LABELS[selectedDeal.stage]}{selectedDeal.priority === 'hot' && ' · Hot'}</span>
                </div>
              </div>
            )}
            <TemplateChips
              dealId={selectedDealId || undefined}
              customerName={selectedDeal?.customer_name}
              onLogged={() => navigate(-1)}
            />
          </div>

          {isReal && deals.length === 0 && (
            <div className="space-y-3 p-4 rounded-md bg-primary/5 border border-primary/30">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                <span>No deals yet. Create your first deal — then log activity against it in seconds.</span>
              </div>
                <NewDealDialog />
            </div>
          )}

          {!isReal && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Customer Name</label>
                <Input placeholder="e.g. Sarah Thompson" value={customer} onChange={e => setCustomer(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Vehicle</label>
                <Input placeholder="e.g. 2022 Toyota RAV4 XLE" value={vehicle} onChange={e => setVehicle(e.target.value)} />
              </div>
            </>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Activity Type {isReal && <span className="text-primary">*</span>}</label>
            <Select value={demoType} onValueChange={v => setDemoType(v as DemoType)}>
              <SelectTrigger><SelectValue placeholder="Select activity type" /></SelectTrigger>
              <SelectContent>
                {Object.entries(DEMO_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notes</label>
            <div className="relative">
              <Textarea
                placeholder="Quick notes about the activity..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                className="pr-12"
              />
              <div className="absolute top-2 right-2">
                <QuickCaptureVoiceButton
                  compact
                  onTranscript={(t) => setNotes(prev => prev ? `${prev} ${t}` : t)}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Tap mic and speak naturally.</p>
          </div>

          <div className="rounded-lg border-l-4 border-l-primary border border-border bg-primary/5 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold">
                Next Step <span className="text-primary">*</span>
              </label>
              <span className="text-[10px] uppercase tracking-wide text-primary/80 font-bold">Required</span>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Every deal must move forward.</p>
            <Select value={nextStep} onValueChange={v => { setNextStep(v as NextStep); setShowWarning(false); }}>
              <SelectTrigger className={showWarning && !nextStep ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select next step (required)" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(NEXT_STEP_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showWarning && !nextStep && (
              <div className="flex items-center gap-2 text-red-400 text-xs">
                <AlertTriangle className="h-3 w-3" /> Next step is required. No deal can be saved without one.
              </div>
            )}
          </div>

          <Button onClick={handleSave} size="lg" className="hidden lg:flex w-full gap-2" disabled={saving}>
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </CardContent>
      </Card>

      {/* Sticky save bar on mobile — sits above MobileNav (h-16 ~64px) */}
      <div className="lg:hidden fixed left-0 right-0 bottom-16 z-40 bg-background/95 backdrop-blur-sm border-t border-border p-3">
        <div className="max-w-lg mx-auto">
          <Button onClick={handleSave} size="lg" className="w-full gap-2 h-12 font-semibold" disabled={saving}>
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
